import React, { useEffect, useState, useCallback } from 'react';
import { message } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Tabs, Tab, Spinner } from 'react-bootstrap';
import { feedbackService, FeedbackPost, FeedbackComment } from '../../services/feedback/feedbackService';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';
import { feedbackCategoryPill, feedbackStatusPill } from './feedbackUi';

const FeedbackAdminReview: React.FC = () => {
  const [posts, setPosts] = useState<FeedbackPost[]>([]);
  const [comments, setComments] = useState<FeedbackComment[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabKey, setTabKey] = useState('posts');
  const navigate = useNavigate();
  const location = useLocation();
  const isPlatformAdmin = location.pathname.startsWith('/platform-admin');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await feedbackService.getReviewQueue();
      setPosts(data.posts || []);
      setComments(data.comments || []);
      setReports(data.reports || []);
    } catch (error: any) {
      message.error(error.message || '获取审核队列失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('确认删除该帖子？删除后前台不可见，审计日志保留。')) return;
    try {
      setLoading(true);
      await feedbackService.deletePost(postId);
      message.success('帖子已删除');
      await fetchData();
    } catch (error: any) {
      message.error(error.message || '删除帖子失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container full-height members-lims fb-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        {isPlatformAdmin ? (
          <>
            <span>平台管理</span>
            <span className="mm-crumb-sep">/</span>
            <span>匿名反馈治理</span>
            <span className="mm-crumb-sep">/</span>
            <span aria-current="page">内容审核</span>
          </>
        ) : (
          <>
            <Link to="/feedback/square" className="fb-crumb-link">
              畅所欲言
            </Link>
            <span className="mm-crumb-sep">/</span>
            <span aria-current="page">审核队列</span>
          </>
        )}
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">内容审核队列</h1>
          <p className="mm-subtitle mb-0">处理待关注的帖子、评论与举报记录。</p>
        </div>
      </header>

      <Tabs activeKey={tabKey} onSelect={(k) => setTabKey(k || 'posts')} className="mb-2 border-0 lims-resource-tabs">
        <Tab eventKey="posts" title={`帖子 (${posts.length})`}>
          <div className="mm-table-shell">
            <div className="mm-sheet-scroll">
              <table className="mm-sheet">
                <thead>
                  <tr>
                    <th>标题</th>
                    <th>类型</th>
                    <th>状态</th>
                    <th className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      举报
                    </th>
                    <th>创建时间</th>
                    <th className="mm-th-actions">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="mm-empty text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        加载中…
                      </td>
                    </tr>
                  ) : posts.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="mm-empty">
                        暂无待审核帖子
                      </td>
                    </tr>
                  ) : (
                    posts.map((record) => (
                      <tr key={record._id} className="mm-row">
                        <td>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start text-decoration-none fb-title-link-btn"
                            onClick={() => navigate(`/feedback/post/${record._id}`)}
                          >
                            <span className="mm-identity-name mb-0">{record.title}</span>
                          </button>
                        </td>
                        <td>{feedbackCategoryPill(record.category)}</td>
                        <td>{feedbackStatusPill(record.status)}</td>
                        <td className="text-end">{record.reportCount ?? 0}</td>
                        <td className="small text-muted">{new Date(record.createdAt).toLocaleString('zh-CN')}</td>
                        <td>
                          <div className="mm-actions">
                            <button
                              type="button"
                              className="mm-icon-action"
                              title="查看"
                              onClick={() => navigate(`/feedback/post/${record._id}`)}
                            >
                              <LimsNavIcon name="visibility" sizePx={22} />
                            </button>
                            <button
                              type="button"
                              className="mm-icon-action"
                              title="删除"
                              onClick={() => handleDeletePost(record._id)}
                            >
                              <LimsNavIcon name="delete_outline" sizePx={22} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>
        <Tab eventKey="comments" title={`评论 (${comments.length})`}>
          <div className="mm-table-shell">
            <div className="mm-sheet-scroll">
              <table className="mm-sheet">
                <thead>
                  <tr>
                    <th>匿名身份</th>
                    <th>内容</th>
                    <th>状态</th>
                    <th className="text-end">举报</th>
                    <th>时间</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="mm-empty text-center py-4">
                        <Spinner animation="border" size="sm" />
                      </td>
                    </tr>
                  ) : comments.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="mm-empty">
                        暂无评论记录
                      </td>
                    </tr>
                  ) : (
                    comments.map((record) => (
                      <tr key={record._id} className="mm-row">
                        <td>{record.anonymousName}</td>
                        <td className="text-break" style={{ maxWidth: 360 }}>
                          {record.content}
                        </td>
                        <td>
                          <span className="fb-cat fb-cat--default">{record.status}</span>
                        </td>
                        <td className="text-end">{record.reportCount ?? 0}</td>
                        <td className="small text-muted">{new Date(record.createdAt).toLocaleString('zh-CN')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>
        <Tab eventKey="reports" title={`举报 (${reports.length})`}>
          <div className="mm-table-shell">
            <div className="mm-sheet-scroll">
              <table className="mm-sheet">
                <thead>
                  <tr>
                    <th>目标类型</th>
                    <th>原因类型</th>
                    <th>状态</th>
                    <th>创建时间</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="mm-empty text-center py-4">
                        <Spinner animation="border" size="sm" />
                      </td>
                    </tr>
                  ) : reports.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="mm-empty">
                        暂无举报
                      </td>
                    </tr>
                  ) : (
                    reports.map((record) => (
                      <tr key={record._id} className="mm-row">
                        <td>{record.targetType}</td>
                        <td>{record.reasonType}</td>
                        <td>
                          <span className="fb-cat fb-cat--default">{record.status}</span>
                        </td>
                        <td className="small text-muted">{new Date(record.createdAt).toLocaleString('zh-CN')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default FeedbackAdminReview;
